import * as DialogPrimitive from '@radix-ui/react-dialog';
import type { VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import type * as React from 'react';
import { useCallback, useState } from 'react';

import { dialogContentVariants } from '@/shared/config/uiVariants';
import { useIsMobile } from '@/shared/hooks/useIsMobile';
import { useVisualViewportStyle } from '@/shared/hooks/useVisualViewportStyle';
import { cn } from '@/shared/lib/cn';
import { DialogPortalContainerContext } from './dialogPortalContext';

/**
 * Modal dialog. Ported from the desktop demo, using the individual
 * `@radix-ui/react-dialog` package. React 19 style — plain function components
 * with `React.ComponentProps<…>` and `ref` forwarded through `...props` (no
 * `forwardRef`/`ElementRef`, which are deprecated), matching the other `ui/`
 * primitives (`avatar`, `alert`, …).
 */

function Dialog(props: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogPortal(props: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose(props: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 z-50 bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Presentation of the dialog surface.
 *
 * - `centered` (default) — the classic centered modal. Unchanged behaviour for
 *   every existing call site.
 * - `responsive` — a bottom drawer below `md` (768px, the same breakpoint
 *   `useIsMobile`/`SectionShell` use) and the centered modal from `md` up. Use
 *   it for dialogs with real content (forms, detail lists) instead of letting a
 *   centered modal shrink to a 320px viewport. Pair it with {@link DialogBody}
 *   so the header and footer stay pinned while the content scrolls.
 * - `mobileKeyboardSafe` — opt-in for a responsive dialog containing a text
 *   input. Below `md`, it pins the drawer to the browser's visual viewport so
 *   iOS virtual keyboards do not cover the active field or footer.
 *
 * Both keep `duration-200`, which `DIALOG_EXIT_ANIMATION_MS` is matched to.
 */
function DialogContent({
  className,
  children,
  variant,
  mobileKeyboardSafe = false,
  showCloseButton = true,
  style,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> &
  VariantProps<typeof dialogContentVariants> & { showCloseButton?: boolean }) {
  const { ref, ...contentProps } = props;
  const isMobile = useIsMobile();
  const keyboardSafe = mobileKeyboardSafe === true;
  const viewportStyle = useVisualViewportStyle(keyboardSafe && isMobile);
  const [portalContainer, setPortalContainer] = useState<HTMLDivElement | null>(null);
  const setContentRef = useCallback(
    (node: HTMLDivElement | null) => {
      setPortalContainer(node);
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref],
  );

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={setContentRef}
        data-slot="dialog-content"
        data-variant={variant ?? 'centered'}
        className={cn(
          dialogContentVariants({ variant, mobileKeyboardSafe: keyboardSafe }),
          className,
        )}
        style={{ ...viewportStyle, ...style }}
        {...contentProps}
      >
        <DialogPortalContainerContext.Provider value={portalContainer}>
          {children}
          {showCloseButton ? (
            <DialogPrimitive.Close className="absolute top-4 right-4 rounded-sm opacity-70 outline-none ring-offset-background transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none [&_svg]:size-4">
              <X />
              <span className="sr-only">Close</span>
            </DialogPrimitive.Close>
          ) : null}
        </DialogPortalContainerContext.Provider>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-1.5 text-center sm:text-left', className)}
      {...props}
    />
  );
}

/**
 * Scrolling content region between {@link DialogHeader} and
 * {@link DialogFooter}. Required by the `responsive` {@link DialogContent}
 * variant: it owns the overflow so the title and the primary action stay
 * reachable when the content is taller than the viewport. The negative inline
 * margin keeps focus rings from being clipped by the scroll container while the
 * content stays aligned with the header.
 */
function DialogBody({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-body"
      className={cn('-mx-6 min-h-0 flex-1 overflow-y-auto px-6 py-1', className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn('flex flex-col-reverse gap-2 sm:flex-row sm:justify-end', className)}
      {...props}
    />
  );
}

function DialogTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('font-semibold text-lg leading-normal tracking-tight', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-muted-foreground text-sm', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
};
